import { useState, useCallback } from 'react';
import { fetchCoverageRowsForContext, type FreightContext } from '@/utils/deliveryContext';
import { checkDeliveryAreaCoverage, CepRange, DeliveryValidationResult } from '@/utils/cepUtils';

export interface UseDeliveryValidationReturn {
  isValidating: boolean;
  validationResult: DeliveryValidationResult | null;
  validateDelivery: (cep: string, supplierId: string, guestCount?: number, context?: FreightContext) => Promise<DeliveryValidationResult>;
  validateDeliveryWithRanges: (cep: string, ranges: CepRange[], guestCount?: number) => Promise<DeliveryValidationResult>;
  clearValidation: () => void;
}

/** Map a DB row to a CepRange */
function rowToCepRange(row: any): CepRange {
  const cells: string[] = Array.isArray(row.celulas) ? row.celulas : [];
  return {
    id: row.id,
    region: row.region_name || '',
    type: cells.length > 0 ? 'cells' : ((row.area_type as 'cep' | 'circle') || 'cep'),
    start: row.cep_start || undefined,
    end: row.cep_end || undefined,
    center: row.latitude != null && row.longitude != null
      ? [Number(row.latitude), Number(row.longitude)]
      : undefined,
    radiusMeters: row.radius_meters ?? undefined,
    cells,
    cellsResolution: row.cells_resolution ?? 7,
    context: (row.context as any) || 'event',
    perKmEnabled: row.per_km_enabled ?? false,
    perKmRate: row.per_km_rate != null ? Number(row.per_km_rate) : 0,
    perKmBaseFee: row.per_km_base_fee != null ? Number(row.per_km_base_fee) : 0,
    perKmMinFee: row.per_km_min_fee != null ? Number(row.per_km_min_fee) : 0,

    deliveryFee: row.delivery_fee != null ? Number(row.delivery_fee) : 0,
    freeDelivery: row.free_delivery ?? false,
    isDisabled: row.is_disabled ?? false,
    allowPickup: row.allow_pickup ?? false,
    deliveryTimeMinutes: row.delivery_time_minutes ?? 30,
    eventSizeSurchargeEnabled: row.event_size_surcharge_enabled ?? false,
    eventSizeSurcharges: Array.isArray(row.event_size_surcharges) 
      ? row.event_size_surcharges.map((s: any) => ({
          minGuests: s.min_guests,
          maxGuests: s.max_guests,
          surcharge: s.surcharge
        }))
      : [],
    distanceRangesEnabled: row.distance_ranges_enabled ?? false,
    distanceRanges: Array.isArray(row.distance_ranges)
      ? row.distance_ranges.map((r: any) => ({
          minKm: r.minKm,
          maxKm: r.maxKm,
          deliveryFee: r.deliveryFee,
          freeDelivery: r.freeDelivery
        }))
      : []
  };
}

export function useDeliveryValidation(): UseDeliveryValidationReturn {
  const [isValidating, setIsValidating] = useState(false);
  const [validationResult, setValidationResult] = useState<DeliveryValidationResult | null>(null);

  const validateDelivery = useCallback(async (
    cep: string,
    supplierId: string,
    guestCount?: number,
    context: FreightContext = 'event'
  ): Promise<DeliveryValidationResult> => {
    setIsValidating(true);
    
    try {
      const data = await fetchCoverageRowsForContext(supplierId, context);

      const ranges: CepRange[] = (data || []).map(rowToCepRange);
      
      if (ranges.length === 0) {
        const result: DeliveryValidationResult = { isValid: true };
        setValidationResult(result);
        return result;
      }
      
      const result = await checkDeliveryAreaCoverage(cep, ranges, guestCount);
      setValidationResult(result);
      return result;
      
    } catch (error) {
      console.error('[DeliveryValidation] Erro:', error);
      // Falha técnica não significa "fora da área": marcamos como não verificado
      // para que a jornada continue e o frete seja confirmado pelo fornecedor.
      const result: DeliveryValidationResult = { isValid: true, isUnverified: true };
      setValidationResult(result);
      return result;
    } finally {
      setIsValidating(false);
    }

  }, []);

  /**
   * Valida a entrega com áreas já carregadas
   */
  const validateDeliveryWithRanges = useCallback(async (
    cep: string,
    ranges: CepRange[],
    guestCount?: number
  ): Promise<DeliveryValidationResult> => {
    setIsValidating(true);
    
    try {
      const result = await checkDeliveryAreaCoverage(cep, ranges, guestCount);
      setValidationResult(result);
      return result;
    } catch (error) {
      console.error('[DeliveryValidation] Erro:', error);
      const result: DeliveryValidationResult = { isValid: true, isUnverified: true };
      setValidationResult(result);
      return result;

    } finally {
      setIsValidating(false);
    }
  }, []);

  const clearValidation = useCallback(() => {
    setValidationResult(null);
  }, []);

  return {
    isValidating,
    validationResult,
    validateDelivery,
    validateDeliveryWithRanges,
    clearValidation,
  };
}
